import { Component, createRef } from "react"
import PropTypes from "prop-types"

import classnames from "classnames"
import Fittty from "shared/components/fittty"
import truncate from "lodash/truncate"
import colors from "foundation/_colors_export.scss?export"
import LoaderDots from "shared/components/loader_dots"

const noop = () => {}

export default class GrantAccessButton extends Component {
  state = {
    copy: this.props.ctaCopy,
    knobBgColor: {
      backgroundColor: `#${this.props.mainService.brand_color}`,
    },
    // Kept in state (with the colour) so the icon tracks the service being
    // authenticated as the flow cycles services, instead of a fixed prop.
    knobIcon: this.props.mainService.lrg_monochrome_image_url || this.props.mainService.lrg_variant_image_url,
    // If the connect slide hasn't run yet, GrantAccess starts it (knob left → right);
    // otherwise it picks up with the knob already right.
    knobRight: !!this.props.animation?.connectSlide?.played,
    sliding: false,
  }

  buttonRef = createRef()

  render() {
    const { copy, knobBgColor, knobIcon, knobRight, sliding } = this.state
    const { dynamicConfig: hasIcon } = this.props

    // Matches the connect_button "Connecting" state. With an icon (dynamic applet)
    // the track stays #111 and the knob carries the service colour + icon; without
    // one the track takes the service colour and the knob turns white.
    const trackStyle = !hasIcon && knobRight ? knobBgColor : { backgroundColor: colors.primaryTextColor }
    const knobStyle = { backgroundColor: !hasIcon && knobRight ? "#fff" : knobBgColor.backgroundColor }

    return (
      <div className="connect-button-outline ga">
        <div
          ref={this.buttonRef}
          style={trackStyle}
          className={classnames("connect-button", { "is-animating": sliding })}
          onClick={this.handleClick}
        >
          <div id="connect-ball" className={classnames("service-icon", { connected: knobRight })} style={knobStyle}>
            {hasIcon && knobIcon && <img src={knobIcon} width="40" height="40" alt="" />}
          </div>
          <div className="connect-cta connected">
            <div>
              <span className="cta-label">
                <Fittty maxSize={this.props.connectButtonMaxFontSize} minSize={14} text={truncate(copy, { length: 24 })} />
              </span>
              {/* Both copies this button shows ("Going to X", "Connecting X") are pending
                  states, so the dots trail them throughout. */}
              <LoaderDots className="connecting-dots" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  handleClick = event => {
    event.preventDefault()
    this.props.onConnectClick()
  }

  // Transitions the interface from one service to the next as each authenticates.
  // If this is the final animation, prev and next are the same props obj.
  startSuccessAnimation = (prev, next, callback = noop) => {
    const svc = next.lastServiceConnected
    const svcName = svc.short_name || svc.name
    this.setState(
      {
        copy: svcName ? `Connecting ${svcName}` : "Connecting…",
        knobBgColor: {
          backgroundColor: `#${svc.brand_color}`,
        },
        knobIcon: svc.lrg_monochrome_image_url || svc.lrg_variant_image_url,
      },
      () => {
        this.successTimer = setTimeout(() => {
          this.setState(
            {
              copy: next.ctaCopy,
              knobBgColor: {
                backgroundColor: `#${next.mainService.brand_color}`,
              },
              knobIcon: next.mainService.lrg_monochrome_image_url || next.mainService.lrg_variant_image_url,
            },
            callback
          )
        }, next.successConnectionDelay)
      }
    )
  }

  componentDidUpdate(prevProps) {
    const { authenticate, lastServiceConnected, animation, mainService, appletIsSaving } = this.props

    if (
      authenticate &&
      lastServiceConnected !== prevProps.lastServiceConnected &&
      (!animation.showSuccessConnection[mainService.module_name] || appletIsSaving)
    ) {
      this.startSuccessAnimation(prevProps, this.props, () => {
        this.props.updateAnimationCol({
          animation: "showSuccessConnection",
          status: { ...{ [mainService.module_name]: true, played: true } },
        })
      })
    }
  }

  componentDidMount() {
    const { autoClick, idleTimeTillAutoClick } = this.props

    // First connecting component: run the knob "turn on" slide (left → right)
    // once, then mark it so ConnectButton starts with the knob already right.
    if (!this.props.animation?.connectSlide?.played) {
      if (this.buttonRef.current) void this.buttonRef.current.offsetWidth
      this.setState({ knobRight: true, sliding: true })
      this.props.updateAnimationCol({ animation: "connectSlide", status: { played: true } })
    }

    // Advance to service auth URL if the user doesn't click first (3s by default)
    if (!this.props.requirementsMet() && autoClick) {
      this.autoClickTimer = setTimeout(() => {
        this.props.onConnectClick()
      }, idleTimeTillAutoClick)
    }
  }

  componentWillUnmount() {
    clearTimeout(this.autoClickTimer)
    clearTimeout(this.successTimer)
  }
}

GrantAccessButton.propTypes = {
  mainService: PropTypes.object.isRequired,
  services: PropTypes.array.isRequired,
  onConnectClick: PropTypes.func.isRequired,
  dynamicConfig: PropTypes.bool,
  ctaCopy: PropTypes.string,
  authenticate: PropTypes.bool,
  lastServiceConnected: PropTypes.object,
  successConnectionDelay: PropTypes.number,
  idleTimeTillAutoClick: PropTypes.number,
  autoClick: PropTypes.bool,
  animation: PropTypes.object.isRequired,
  updateAnimationCol: PropTypes.func.isRequired,
  requirementsMet: PropTypes.func,
  appletIsSaving: PropTypes.bool,
  connectButtonMaxFontSize: PropTypes.number,
}

GrantAccessButton.defaultProps = {
  successConnectionDelay: 2000,
  autoClick: true,
  idleTimeTillAutoClick: 3000,
  authenticate: true,
  connectButtonMaxFontSize: 34,
}
